import { usePublicChatContext } from "@lazarentertainment/public-chat-web/dist/src/react"
import { useEffect, useRef, useState } from "react"
import { FormattedMessage } from "react-intl"
import { AccentButton } from "shared/components/buttons/AccentButton"
import {
  TextInput,
  TextInputWrapper,
} from "shared/components/inputs/styles"
import { useComponentId } from "shared/hooks/useComponentId"
import { P } from "shared/styles/textStyles"
import styled, { DataAttributes } from "styled-components"

import {
  ModalText,
  PanelModal,
} from "../../../shared/components/PanelModal"

import { useChatContext } from "./ChatContext"

const Form = styled.form`
  display: flex;
  flex-wrap: wrap;
  line-height: normal;
  justify-content: center;
  align-items: center;

  button {
    margin-left: 5px;
    margin-bottom: 5px;
  }

  ${TextInputWrapper} {
    flex-basis: 120px;
    flex-grow: 2;
    margin-bottom: 5px;
  }

  ${TextInput} {
    &::placeholder {
      color: ${(props) => props.theme.colors.med};
    }
  }
`

const EnterButton = styled(AccentButton).attrs<DataAttributes>({
  "data-robot": "join-chat-button",
})`
  font-weight: 400;
  font-family: ${(props) => props.theme.font.primary};
  text-transform: unset;
`

const Error = styled.div`
  margin-bottom: 8px;
  width: 100%;

  ${P} {
    width: 100%;
    font-size: ${(props) => props.theme.input.errorText};
    color: ${(props) => props.theme.colors.error};
    text-align: center;
  }
`

type JoinChatModalProps = {
  focusMainChatInput?: () => void
  onClose: () => void
}

const JoinChatModal = ({
  focusMainChatInput,
  onClose,
}: JoinChatModalProps) => {
  const { chatError } = useChatContext()
  const { updateDisplayName, displayName } = usePublicChatContext()
  const [nameInputValue, setNameInputValue] = useState(displayName ?? "")
  const ref = useRef<HTMLInputElement | null>(null)
  const componentId = useComponentId()
  const formId = `join-chat-form-${componentId}`

  const submitName = (event) => {
    event.preventDefault()

    // Focus handling in this component is confusing but it's here to work around
    // the fact that iOS Safari will close the keyboard even when calling .focus()
    // on an input. On mobile devices, once the name is set, we want to return focus to the main
    // chat input, keeping the keyboard on screen. However, Safari does not allow you
    // to do that in async code. The result is that if we call focus on the main chat input
    // after waiting for the name to get set, the input receives focus but the keyboard closes.
    // This is confusing for users because the input should not be visible when the keyboard
    // is not open. iOS Safari does however, allow you to _move_ focus from one input to another.
    // In order to achieve this though, we can't ever move focus away from an input.
    //
    // 1. When the Join Modal is opened, it's in response to a user-action so we can autofucs
    //    the chat input.
    // 2. When the user presses enter we immediately move focus back to the main chat input.
    //    They keyboard remains open in this case.
    // 3. If the name change is a success then the modal closes and we're done.
    // 4. If the name change is a failure then we can move focus back to the join modal input.
    //    This keeps the keyboard open because focus was previously on a different input. We're
    //    simply moving focus from one input to another.
    // 5. Repeat.
    //
    // This is based on the solution listed here: https://stackoverflow.com/a/45703019/3554402
    if (focusMainChatInput) {
      focusMainChatInput()
    }

    updateDisplayName(nameInputValue.trim())
  }

  useEffect(() => {
    // Point number (4) above. If there is a failure in setting the name then move focus back
    // into the name change input.
    if (chatError?.code) {
      ref.current?.focus()
    }
  }, [chatError?.code])

  return (
    <PanelModal
      closeModal={onClose}
      bottom
      closeButtonRobotTag="chat-name-modal-close-button"
    >
      <ModalText>
        <FormattedMessage
          id="chat.joinMessage"
          defaultMessage="Please enter your name."
        />
      </ModalText>
      <Form onSubmit={submitName} id={formId}>
        <TextInputWrapper>
          <TextInput
            ref={ref}
            type="text"
            autoFocus
            maxLength={20}
            id="name-input"
            placeholder="Your Name"
            onChange={(event) => setNameInputValue(event.target.value)}
            value={nameInputValue}
            onFocus={({ target }) => target.select()}
          />
        </TextInputWrapper>
        {chatError?.code === "DisplayNameRejectedError" && (
          <Error>
            <P>
              <FormattedMessage
                id="chat.nameUnavailable"
                defaultMessage="That name is not available."
              />
            </P>
            <P>
              <FormattedMessage
                id="chat.chooseDifferentName"
                defaultMessage="Please choose a different name."
              />
            </P>
          </Error>
        )}
        <EnterButton
          type="submit"
          disabled={nameInputValue.trim().length === 0}
          form={formId}
        >
          <FormattedMessage id="chat.Enter" defaultMessage="Enter" />
        </EnterButton>
      </Form>
    </PanelModal>
  )
}

export default JoinChatModal
